// CaseHistoryConfig TypeId reference:
//   0 = Custom History
//   1 = General History
//   2 = Pediatric History
//   3 = MVC History
export interface CaseHistoryConfig {
  typeId: number
  description: string
  portalConfigCode: string
}

export interface CaseHistoryPortalConfigData {
  portalConfigID: number
  /** "Data" = portal config template JSON; "Answers" = patient questionnaire answers */
  code: string | null
  value: string | null
}

export interface CaseHistoryPortalConfig {
  id: number
  typeId: number
  code: string | null
  description: string | null
  data: CaseHistoryPortalConfigData[] | null
  /** True when the linked visit document is signed (portal session write-locked). */
  locked?: boolean
}

/** Swagger: CaseHistoryTaskAction */
export interface CaseHistoryTaskAction {
  insInfo: boolean
  caseHistoryConfigs: CaseHistoryConfig[]
  error: string | null
}

/** Swagger: FormTaskAction */
export interface FormTaskAction {
  facilityFormID: number
  formName: string | null
  patientName: string | null
  error: string | null
}

/** Swagger: FvTaskAction */
export interface FvTaskAction {
  fileIDs: number[] | null
  error: string | null
}

/** Swagger: StatementTaskAction */
export interface StatementTaskAction {
  statementBatchID: number
  statementDate: string | null
  fileID: number
  totalDue: number
  pmtDueDate: string | null
  balanceContractID: number
  error: string | null
  /** Still owed: totalDue less payments applied to this statement (set by the API). */
  balanceDue?: number
  /** The facility takes Fortis payments in the portal and the task is open (set by the API). */
  canPayOnline?: boolean
}

/** Swagger: ApiValue — POST /Portal/GetCaseHistoryValue response body */
export interface ApiValue {
  success: boolean
  reason: string | null
  value: string | null
}

/** Swagger: ApiBoolResult */
export interface ApiBoolResult {
  success: boolean
  reason: string | null
  locked?: boolean
  error?: string | null
}

/** Swagger: ApiIdResult */
export interface ApiIdResult {
  success: boolean
  reason: string | null
  id: number
  locked?: boolean
  error?: string | null
}

/** Metadata extracted from the top-level fields of a PortalConfig JSON wire template. */
export interface PortalConfigMeta {
  id: string
  name: string
  complaintBehavior: 'include' | 'hide'
  generalBehavior: 'include' | 'hide'
  position: 'before_complaint' | 'after_complaint' | 'after_general'
  /** Most complaints the patient may add (1-16). */
  complaintLimit: number
  /** Ask the 1-10 rating (VAS / CurrentSeverity) on each complaint. */
  askVas: boolean
  /** Most distinct outcome assessments asked across the patient's complaints (0-4; 0 = none). */
  outcomeAssessmentLimit: number
  /**
   * With only the General History config attached: complaints before General + Family (true) or
   * after them (false). Ignored when a custom config is attached; position orders the steps then.
   */
  complaintsFirst: boolean
}

const DEFAULT_COMPLAINT_LIMIT = 4
const DEFAULT_ASK_VAS = true
const DEFAULT_OUTCOME_ASSESSMENT_LIMIT = 2
const DEFAULT_COMPLAINTS_FIRST = true

/** Defaults for the case-history limits when a config omits them. */
export const DEFAULT_PORTAL_CONFIG_LIMITS = {
  complaintLimit: DEFAULT_COMPLAINT_LIMIT,
  askVas: DEFAULT_ASK_VAS,
  outcomeAssessmentLimit: DEFAULT_OUTCOME_ASSESSMENT_LIMIT,
  complaintsFirst: DEFAULT_COMPLAINTS_FIRST
} as const

/** Whole number in [min, max] from a number or numeric string; anything else gives the fallback. */
function asClampedInt(v: unknown, min: number, max: number, fallback: number): number {
  let n: number
  if (typeof v === 'number') n = v
  else if (typeof v === 'string' && v.trim() !== '') n = Number(v.trim())
  else return fallback
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, Math.trunc(n)))
}

function asBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (s === 'true') return true
    if (s === 'false') return false
  }
  return fallback
}

/*
 * The limits only apply to a portal-config flow (meta present). Without a config the built-in flow
 * keeps its old behavior: no complaint cap, the rating is asked, and every outcome assessment shows.
 * Meta is persisted in localStorage, so a meta saved before these fields existed falls back per
 * field to the defaults.
 */

/** Most complaints the patient may add; unlimited when no portal config drives the flow. */
export function complaintLimitFor(meta: PortalConfigMeta | null | undefined): number {
  if (!meta) return Number.POSITIVE_INFINITY
  return asClampedInt(meta.complaintLimit, 1, 16, DEFAULT_COMPLAINT_LIMIT)
}

/** Whether to ask the 1-10 rating; always asked when no portal config drives the flow. */
export function askVasFor(meta: PortalConfigMeta | null | undefined): boolean {
  if (!meta) return true
  return asBool(meta.askVas, DEFAULT_ASK_VAS)
}

/** Most distinct outcome assessments; null (no trimming) when no portal config drives the flow. */
export function outcomeAssessmentLimitFor(
  meta: PortalConfigMeta | null | undefined
): number | null {
  if (!meta) return null
  return asClampedInt(meta.outcomeAssessmentLimit, 0, 4, DEFAULT_OUTCOME_ASSESSMENT_LIMIT)
}

/** Complaints before General + Family; true without a config or for a meta saved before the field. */
export function complaintsFirstFor(meta: PortalConfigMeta | null | undefined): boolean {
  if (!meta) return true
  return asBool(meta.complaintsFirst, DEFAULT_COMPLAINTS_FIRST)
}

/**
 * Keep the outcome assessments for the first `limit` distinct oatIDs, in the order given (the API's
 * order across the patient's complaints). A null limit keeps everything.
 */
export function limitDistinctOats<T extends { oatID: number }>(
  oats: T[],
  limit: number | null
): T[] {
  if (limit == null) return oats
  const allowed = new Set<number>()
  for (const o of oats) {
    if (allowed.size >= limit) break
    allowed.add(o.oatID)
  }
  return oats.filter((o) => allowed.has(o.oatID))
}

function asString(v: unknown, fallback: string): string {
  if (typeof v === 'string' && v.trim()) return v.trim()
  return fallback
}

function resolveActiveHistoryTemplate(doc: Record<string, unknown>): Record<string, unknown> {
  const templates = doc.historyTemplates ?? doc.HistoryTemplates
  if (!Array.isArray(templates) || templates.length === 0) return {}
  const active = templates.find(
    (t: Record<string, unknown>) => t.isActive === true || t.IsActive === true
  )
  return (active ?? templates[0]) as Record<string, unknown>
}

/** Extract flow-control metadata from a parsed PortalConfig JSON template. */
export function parsePortalConfigMeta(doc: Record<string, unknown>): PortalConfigMeta {
  const tpl = resolveActiveHistoryTemplate(doc)

  const id = asString(tpl.id ?? tpl.Id ?? doc.id ?? doc.Id, '')
  const name = asString(tpl.name ?? tpl.Name ?? doc.name ?? doc.Name ?? doc.title ?? doc.Title, '')

  const rawComplaint = asString(
    tpl.complaintBehavior ??
      tpl.ComplaintBehavior ??
      doc.complaintBehavior ??
      doc.ComplaintBehavior,
    'include'
  )
  const complaintBehavior: PortalConfigMeta['complaintBehavior'] =
    rawComplaint === 'hide' ? 'hide' : 'include'

  const rawGeneral = asString(
    tpl.generalBehavior ?? tpl.GeneralBehavior ?? doc.generalBehavior ?? doc.GeneralBehavior,
    'include'
  )
  const generalBehavior: PortalConfigMeta['generalBehavior'] =
    rawGeneral === 'hide' ? 'hide' : 'include'

  const rawPosition = asString(
    tpl.position ?? tpl.Position ?? doc.position ?? doc.Position,
    'before_complaint'
  )
  const position: PortalConfigMeta['position'] =
    rawPosition === 'after_complaint'
      ? 'after_complaint'
      : rawPosition === 'after_general'
        ? 'after_general'
        : 'before_complaint'

  const complaintLimit = asClampedInt(
    tpl.complaintLimit ?? tpl.ComplaintLimit ?? doc.complaintLimit ?? doc.ComplaintLimit,
    1,
    16,
    DEFAULT_COMPLAINT_LIMIT
  )
  const askVas = asBool(tpl.askVas ?? tpl.AskVas ?? doc.askVas ?? doc.AskVas, DEFAULT_ASK_VAS)
  const outcomeAssessmentLimit = asClampedInt(
    tpl.outcomeAssessmentLimit ??
      tpl.OutcomeAssessmentLimit ??
      doc.outcomeAssessmentLimit ??
      doc.OutcomeAssessmentLimit,
    0,
    4,
    DEFAULT_OUTCOME_ASSESSMENT_LIMIT
  )
  const complaintsFirst = asBool(
    tpl.complaintsFirst ?? tpl.ComplaintsFirst ?? doc.complaintsFirst ?? doc.ComplaintsFirst,
    DEFAULT_COMPLAINTS_FIRST
  )

  return {
    id,
    name,
    complaintBehavior,
    generalBehavior,
    position,
    complaintLimit,
    askVas,
    outcomeAssessmentLimit,
    complaintsFirst
  }
}
